/*
 * fm-sort-select.js — custom "Sort by" dropdown for the desktop collection page.
 * 05-Aug-2026.
 *
 * WHY THIS EXISTS. The sort control is a native `<select>`. `appearance: none`
 * (already set in component-facets.css) restyles the CLOSED control, but the
 * OPEN option list is drawn by the operating system and CSS cannot reach it —
 * on macOS that is the grey Aqua popup, which is what looked out of place
 * against the rest of the page. There is no CSS fix: styling the open list
 * needs a replacement listbox.
 *
 * WHAT IT DOES NOT DO — the native `<select>` STAYS IN THE DOM, keeps its
 * `name="sort_by"`, and remains the single source of truth. This script only
 * adds a button + list in front of it and writes back to it. That matters
 * because:
 *   • `#FacetSortForm` is serialised by `facets.js` (`createSearchParams`), so
 *     the select must still be a submittable form control. It is hidden with
 *     CSS, never `disabled` and never renamed — a disabled or unnamed control
 *     is dropped from the form and sorting would silently stop working.
 *   • With JS off or broken, the native select is still there and still works.
 *     The custom UI is added by this script, so its absence is the fallback
 *     rather than a dead control.
 *
 * HOW A CHOICE IS APPLIED. Set `select.value`, then dispatch `input` AND
 * `change`, both bubbling — which is what a real native selection fires.
 * `facets.js` binds `input` on the form (debounced 800ms) and NOT `change`, so
 * `input` is the one that actually triggers the AJAX re-render; `change` is
 * dispatched anyway so the emulation is faithful for anything else listening.
 * The 800ms debounce collapses the pair into one request.
 *
 * MEASURED, on /collections/entrance-mats: setting the value and dispatching
 * `input` re-rendered the grid AND pushed `sort_by=price-descending` into the
 * URL, so sort stays shareable and back-navigable. This deliberately keeps the
 * existing desktop AJAX path rather than copying the mobile sheet's
 * navigate-to-URL approach — the mobile sheet does that because a control
 * outside `#FacetFiltersFormMobile` cannot round-trip through facets.js (see
 * fm-mobile-collection.js), which is not a problem the desktop control has:
 * its form is `#FacetSortForm`, one of the three ids `onSubmitHandler` accepts
 * on the non-mobile path.
 *
 * WHY NOT id-BASED. `id="SortBy"` IS DUPLICATED on this page — the desktop sort
 * row and the mobile filter drawer both use it, and there is a third select
 * with `id="SortBy-mobile"`. So `getElementById`/`querySelector('#SortBy')`
 * silently reaches only the first of them. Everything here works from
 * `select[name="sort_by"]` and skips anything inside `.mobile-facets__wrapper`.
 *
 * THE MOBILE CONTROLS ARE LEFT NATIVE ON PURPOSE. Mobile already has its own
 * custom sheet (`fm-sortsheet`, 28-Jul), and where a native picker does show on
 * a touch device it is the platform control users expect — a bespoke listbox
 * would be worse there, not better.
 *
 * RE-RENDER SAFETY — THIS ROW *IS* REPLACED, SO THE OBSERVER BELOW IS REQUIRED.
 * It is worth being precise about, because the obvious audit gets it wrong.
 * Checking the parents of the replaced `.js-filter` blocks
 * (`#FacetFiltersForm` / `#FacetFiltersFormMobile` / `#FacetFiltersPillsForm`)
 * and the two count containers (`#ProductCount`, `#ProductCountDesktop`) says
 * the desktop sort select is in none of them — which is true, and which is why
 * this script originally shipped without an observer and was WRONG.
 *
 * The path that actually destroys it is `renderAdditionalElements`:
 *
 *     const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
 *     ...document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
 *
 * The desktop control sits inside `<div class="facet-filters sorting caption">`,
 * so that third selector wipes it on EVERY filter and sort change. Symptom, if
 * this regresses: sorting works once, then the control silently reverts to the
 * native select — because the server HTML that replaced it never had the custom
 * markup in the first place. Note also that the theme uses `querySelector`, not
 * `querySelectorAll`, so only the FIRST `.sorting` in the document is replaced;
 * the observer is attached to all of them rather than assuming which one that is.
 *
 * The observer cannot loop: its own callback calls `run()`, `enhance()` sees the
 * `data-fm-sortsel` marker on an already-enhanced select and returns without
 * touching the DOM, so the second pass produces no mutations and the cycle ends.
 * After a replacement the select is a NEW element with no marker, which is
 * exactly when re-enhancing is wanted.
 *
 * NOT IMPLEMENTED: type-ahead (typing "p" to jump to "Price, low to high").
 * Native selects do it; this does not. Nine options in a visible list is a
 * small enough set that arrow keys cover it.
 */
(function () {
  'use strict';

  var uid = 0;

  function enhance(select) {
    /* Idempotent: a second call on the same select is a no-op rather than a
       second button stacked on the first. */
    if (select.getAttribute('data-fm-sortsel') === 'on') return;

    var wrap = select.closest('.select');
    if (!wrap) return;

    select.setAttribute('data-fm-sortsel', 'on');
    uid += 1;

    var listId = 'FmSortSelList-' + uid;
    var optIdBase = 'FmSortSelOpt-' + uid + '-';

    var box = document.createElement('div');
    box.className = 'fm-sortsel';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fm-sortsel__btn';
    btn.setAttribute('aria-haspopup', 'listbox');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', listId);
    /* The visible "Sort by:" label next to this control uses `for="SortBy"`,
       which is unreliable here because that id is duplicated — so the button
       carries its own accessible name and the current value is read from its
       text content. */
    btn.setAttribute('aria-label', 'Sort by');

    var valueEl = document.createElement('span');
    valueEl.className = 'fm-sortsel__value';

    var chev = document.createElement('span');
    chev.className = 'fm-sortsel__chev';
    chev.setAttribute('aria-hidden', 'true');

    btn.appendChild(valueEl);
    btn.appendChild(chev);

    var list = document.createElement('ul');
    list.className = 'fm-sortsel__list';
    list.id = listId;
    list.setAttribute('role', 'listbox');
    list.setAttribute('tabindex', '-1');
    list.hidden = true;

    var rows = [];
    Array.prototype.forEach.call(select.options, function (opt, i) {
      var li = document.createElement('li');
      li.className = 'fm-sortsel__opt';
      li.id = optIdBase + i;
      li.setAttribute('role', 'option');
      li.setAttribute('data-value', opt.value);
      li.textContent = opt.text.trim();
      li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
      if (opt.selected) li.classList.add('is-selected');
      list.appendChild(li);
      rows.push(li);
    });

    if (!rows.length) return;

    var activeIndex = Math.max(0, select.selectedIndex);

    function syncButton() {
      var opt = select.options[select.selectedIndex];
      valueEl.textContent = opt ? opt.text.trim() : '';
    }

    function setActive(i) {
      if (i < 0) i = 0;
      if (i > rows.length - 1) i = rows.length - 1;
      activeIndex = i;
      rows.forEach(function (r, n) {
        r.classList.toggle('is-active', n === i);
      });
      list.setAttribute('aria-activedescendant', rows[i].id);
      /* `block: 'nearest'` so opening the list does not jump the page when the
         selected option is already visible. */
      if (rows[i].scrollIntoView) rows[i].scrollIntoView({ block: 'nearest' });
    }

    var isOpen = false;

    function open() {
      if (isOpen) return;
      isOpen = true;
      list.hidden = false;
      box.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');

      /* Flip above the button when there is not room below. The sort row sits
         about 610px down a collection page, so on a 900px viewport a 334px list
         opened BEFORE the user scrolls runs ~89px off the bottom of the screen.
         Measured while `hidden` is already false — a hidden element has no box,
         so this has to happen after the reveal and before anything that could
         scroll. */
      box.classList.remove('is-above');
      var btnRect = btn.getBoundingClientRect();
      var listH = list.getBoundingClientRect().height;
      var roomBelow = window.innerHeight - btnRect.bottom;
      var roomAbove = btnRect.top;
      if (roomBelow < listH + 12 && roomAbove > roomBelow) {
        box.classList.add('is-above');
      }

      setActive(select.selectedIndex < 0 ? 0 : select.selectedIndex);
      /* `preventScroll` because focusing the list otherwise scrolls the page to
         it — which moves the control the user just clicked, and also silently
         invalidates any geometry measured either side of the call. */
      list.focus({ preventScroll: true });
    }

    function close(returnFocus) {
      if (!isOpen) return;
      isOpen = false;
      list.hidden = true;
      box.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      list.removeAttribute('aria-activedescendant');
      if (returnFocus) btn.focus();
    }

    function commit(i) {
      var opt = select.options[i];
      if (!opt) return;

      var changed = select.selectedIndex !== i;
      select.selectedIndex = i;

      rows.forEach(function (r, n) {
        r.setAttribute('aria-selected', n === i ? 'true' : 'false');
        r.classList.toggle('is-selected', n === i);
      });
      syncButton();
      close(true);

      /* Nothing to submit if the value did not move — a re-submit would cost a
         needless network round-trip and a grid flash. */
      if (!changed) return;

      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    btn.addEventListener('click', function () {
      if (isOpen) close(true);
      else open();
    });

    btn.addEventListener('keydown', function (event) {
      var k = event.key;
      if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ' || k === 'Spacebar') {
        event.preventDefault();
        open();
      }
    });

    list.addEventListener('keydown', function (event) {
      var k = event.key;
      if (k === 'Escape') {
        event.preventDefault();
        close(true);
      } else if (k === 'ArrowDown') {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (k === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex - 1);
      } else if (k === 'Home') {
        event.preventDefault();
        setActive(0);
      } else if (k === 'End') {
        event.preventDefault();
        setActive(rows.length - 1);
      } else if (k === 'Enter' || k === ' ' || k === 'Spacebar') {
        event.preventDefault();
        commit(activeIndex);
      } else if (k === 'Tab') {
        /* Let the browser move focus naturally, but do not leave an orphaned
           open list behind. No returnFocus — that would fight the Tab. */
        close(false);
      }
    });

    /* `mousedown` rather than `click`: the list is focused, and a click on an
       option would otherwise blur it first, closing the list before the click
       landed. */
    list.addEventListener('mousedown', function (event) {
      var row = event.target.closest('.fm-sortsel__opt');
      if (!row) return;
      event.preventDefault();
      commit(rows.indexOf(row));
    });

    list.addEventListener('mousemove', function (event) {
      var row = event.target.closest('.fm-sortsel__opt');
      if (row) setActive(rows.indexOf(row));
    });

    document.addEventListener('mousedown', function (event) {
      if (!isOpen) return;
      if (!box.contains(event.target)) close(false);
    });

    /* If anything else moves the select (browser restoring form state on a back
       navigation, or another script), keep the button honest. */
    select.addEventListener('change', function () {
      syncButton();
      rows.forEach(function (r, n) {
        r.setAttribute('aria-selected', n === select.selectedIndex ? 'true' : 'false');
        r.classList.toggle('is-selected', n === select.selectedIndex);
      });
    });

    syncButton();
    box.appendChild(btn);
    box.appendChild(list);
    wrap.appendChild(box);
    /* Marks the wrapper so the CSS can hide the native control ONLY once the
       replacement is actually in the DOM. Without this the native select would
       be hidden by a stylesheet that loads whether or not this script ran,
       leaving no sort control at all if it failed. */
    wrap.classList.add('fm-sortsel-host');
  }

  function run() {
    var selects = document.querySelectorAll('select[name="sort_by"]');
    Array.prototype.forEach.call(selects, function (select) {
      /* Mobile keeps its native pickers and its own sort sheet. */
      if (select.closest('.mobile-facets__wrapper')) return;
      if (select.id === 'SortBy-mobile') return;
      enhance(select);
    });
  }

  var observed = [];

  function watch() {
    var pending;
    var rerun = function () {
      clearTimeout(pending);
      /* Debounced so the burst of mutations from one innerHTML swap costs a
         single pass, matching how fm-mobile-collection.js handles the same
         problem on the filter drawer. */
      pending = setTimeout(run, 120);
    };

    Array.prototype.forEach.call(document.querySelectorAll('.sorting'), function (row) {
      if (observed.indexOf(row) !== -1) return;
      observed.push(row);
      new MutationObserver(rerun).observe(row, { childList: true, subtree: true });
    });
  }

  function boot() {
    run();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('fm:sort-select:refresh', boot);
})();
